import { useMemo, useState, type ReactNode } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Cloud,
  Folder,
  Globe,
  Loader2,
  Search,
  X,
} from 'lucide-react';
import { Modal } from '@/design-system';
import { cn } from '@/shared/cn';
import {
  BUTTON_PRIMARY,
  FIELD_INPUT,
  MENU_SECTION_LABEL,
} from '@/shared/ui-class';
import { SIDEBAR_ROW_ACTIVE } from '@/versions/v1/chrome/sidebar/SidebarShell';

type SharePointNode = {
  id: string;
  name: string;
  kind: 'site' | 'library' | 'folder' | 'file';
  children?: SharePointNode[];
};

type SelectedItem = {
  id: string;
  name: string;
};

type LocationKey = 'onedrive' | 'followed-1' | 'followed-2' | 'org' | 'all-1' | 'all-2' | 'all-3';

const FOLLOWED_SITES = [
  { id: 'followed-1', name: 'Product team' },
  { id: 'followed-2', name: 'Client workspace' },
] as const;

const ALL_SITES = [
  { id: 'all-1', name: 'Field study hub' },
  { id: 'all-2', name: 'Research archive' },
  { id: 'all-3', name: 'Partner portal' },
] as const;

const ORG_SITE = { id: 'org', name: 'Airis Analytica' };

const SEED_TREES: Record<string, SharePointNode[]> = {
  onedrive: [
    {
      id: 'od-lib',
      name: 'Documents',
      kind: 'library',
      children: [
        {
          id: 'od-folder-1',
          name: 'Drafts',
          kind: 'folder',
          children: [
            { id: 'od-file-1', name: 'Notes.docx', kind: 'file' },
            { id: 'od-file-2', name: 'Screenshot.png', kind: 'file' },
          ],
        },
      ],
    },
  ],
  'followed-1': [
    {
      id: 'f1-lib',
      name: 'Shared documents',
      kind: 'library',
      children: [
        {
          id: 'f1-folder',
          name: 'Roadmaps',
          kind: 'folder',
          children: [{ id: 'f1-file', name: 'Q3 roadmap.pptx', kind: 'file' }],
        },
      ],
    },
  ],
  'followed-2': [
    {
      id: 'f2-lib',
      name: 'Deliverables',
      kind: 'library',
      children: [{ id: 'f2-file', name: 'Kickoff deck.pdf', kind: 'file' }],
    },
  ],
  org: [
    {
      id: 'org-lib',
      name: 'Documents',
      kind: 'library',
      children: [
        {
          id: 'org-folder',
          name: 'Client deliverables',
          kind: 'folder',
          children: [
            { id: 'org-file-1', name: 'Foundation parity brief.docx', kind: 'file' },
            { id: 'org-file-2', name: 'Stage 2 kickoff.pdf', kind: 'file' },
          ],
        },
      ],
    },
  ],
  'all-1': [
    {
      id: 'a1-lib',
      name: 'Site library',
      kind: 'library',
      children: [{ id: 'a1-file', name: 'Field notes.docx', kind: 'file' }],
    },
  ],
  'all-2': [
    {
      id: 'a2-lib',
      name: 'Research',
      kind: 'library',
      children: [{ id: 'a2-file', name: 'Competitive scan.xlsx', kind: 'file' }],
    },
  ],
  'all-3': [
    {
      id: 'a3-lib',
      name: 'Shared',
      kind: 'library',
      children: [{ id: 'a3-file', name: 'Partner brief.pdf', kind: 'file' }],
    },
  ],
};

const FLAT_SEARCH_RESULTS: SelectedItem[] = [
  { id: 'search-1', name: 'Foundation parity brief.docx' },
  { id: 'search-2', name: 'Stage 2 kickoff.pdf' },
  { id: 'search-3', name: 'Competitive scan.xlsx' },
  { id: 'search-4', name: 'Q3 roadmap.pptx' },
  { id: 'search-5', name: 'Meeting notes.md' },
];

type SharePointBrowseModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAttach: (fileNames: string[]) => void;
};

export function SharePointBrowseModal({
  open,
  onOpenChange,
  onAttach,
}: SharePointBrowseModalProps) {
  const [location, setLocation] = useState<LocationKey | null>(null);
  const [path, setPath] = useState<SharePointNode[]>([]);
  const [query, setQuery] = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const [selected, setSelected] = useState<Map<string, SelectedItem>>(() => new Map());
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ index: number; total: number } | null>(
    null,
  );

  const current = path[path.length - 1] ?? null;
  const rootEntries = location ? SEED_TREES[location] ?? [] : [];
  const browseEntries = useMemo(() => {
    const list = current?.children ?? rootEntries;
    const needle = query.trim().toLowerCase();
    if (!needle || searchActive) return list;
    return list.filter((entry) => entry.name.toLowerCase().includes(needle));
  }, [current, rootEntries, query, searchActive]);

  const searchResults = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return FLAT_SEARCH_RESULTS;
    return FLAT_SEARCH_RESULTS.filter((item) => item.name.toLowerCase().includes(needle));
  }, [query]);

  const reset = () => {
    setLocation(null);
    setPath([]);
    setQuery('');
    setSearchActive(false);
    setSelected(new Map());
    setImporting(false);
    setImportProgress(null);
  };

  const toggleSelection = (item: SelectedItem) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.set(item.id, item);
      return next;
    });
  };

  const runImport = async () => {
    const items = Array.from(selected.values());
    if (items.length === 0) return;
    setImporting(true);
    for (let index = 0; index < items.length; index += 1) {
      setImportProgress({ index: index + 1, total: items.length });
      await new Promise((resolve) => window.setTimeout(resolve, 400));
    }
    onAttach(items.map((item) => item.name));
    setImporting(false);
    setImportProgress(null);
    onOpenChange(false);
    reset();
  };

  const locationLabel =
    location === 'onedrive'
      ? 'Your files'
      : location === 'org'
        ? ORG_SITE.name
        : FOLLOWED_SITES.find((s) => s.id === location)?.name ??
          ALL_SITES.find((s) => s.id === location)?.name ??
          'Sites';

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next && importing) return;
        onOpenChange(next);
        if (!next) reset();
      }}
      title="Browse SharePoint"
      className="!max-w-[min(960px,calc(100vw-2rem))]"
    >
      <div className="flex h-[min(520px,calc(100vh-8rem))] flex-col gap-0 overflow-hidden px-6 pb-6">
        <div className="flex min-h-0 flex-1 overflow-hidden rounded-lg border border-[var(--glass-border)]">
          <aside
            className="flex w-[min(220px,32%)] shrink-0 flex-col overflow-y-auto border-r border-[var(--glass-border)] bg-[color-mix(in_srgb,var(--color-zinc-50)_50%,transparent)] dark:bg-[color-mix(in_srgb,var(--color-zinc-900)_30%,transparent)]"
            aria-label="SharePoint locations"
          >
            <LocationSection label="OneDrive">
              <LocationRow
                active={location === 'onedrive'}
                icon={<Cloud className="h-4 w-4" />}
                label="Your files"
                onClick={() => {
                  setLocation('onedrive');
                  setPath([]);
                  setSearchActive(false);
                }}
              />
            </LocationSection>
            <LocationSection label="Followed sites">
              {FOLLOWED_SITES.map((site) => (
                <LocationRow
                  key={site.id}
                  active={location === site.id}
                  icon={<Globe className="h-4 w-4" />}
                  label={site.name}
                  onClick={() => {
                    setLocation(site.id as LocationKey);
                    setPath([]);
                    setSearchActive(false);
                  }}
                />
              ))}
            </LocationSection>
            <LocationSection label="Organization">
              <LocationRow
                active={location === 'org'}
                icon={<Globe className="h-4 w-4" />}
                label={ORG_SITE.name}
                onClick={() => {
                  setLocation('org');
                  setPath([]);
                  setSearchActive(false);
                }}
              />
            </LocationSection>
            <LocationSection label="All sites" className="min-h-0 flex-1">
              {ALL_SITES.map((site) => (
                <LocationRow
                  key={site.id}
                  active={location === site.id}
                  icon={<Globe className="h-4 w-4" />}
                  label={site.name}
                  onClick={() => {
                    setLocation(site.id as LocationKey);
                    setPath([]);
                    setSearchActive(false);
                  }}
                />
              ))}
            </LocationSection>
          </aside>

          <div className="flex min-w-0 flex-1 flex-col">
            <div className="border-b border-[var(--glass-border)] p-3">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setSearchActive(event.target.value.trim().length > 0);
                  }}
                  placeholder="Search documents across SharePoint"
                  className={cn(FIELD_INPUT, 'pl-9')}
                />
              </label>
            </div>

            <div className="scrollbar-subtle min-h-0 flex-1 overflow-y-auto p-3">
              {searchActive ? (
                <ul className="space-y-1">
                  {searchResults.map((hit) => {
                    const checked = selected.has(hit.id);
                    return (
                      <li key={hit.id}>
                        <button
                          type="button"
                          className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm hover:bg-zinc-100 dark:hover:bg-zinc-900"
                          onClick={() => toggleSelection(hit)}
                        >
                          <input type="checkbox" checked={checked} readOnly className="pointer-events-none" />
                          <span className="truncate">{hit.name}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : !location ? (
                <p className="text-sm text-muted">
                  Select OneDrive or a site from the sidebar to browse files.
                </p>
              ) : (
                <>
                  <div className="mb-3 flex flex-wrap items-center gap-1 text-xs text-muted">
                    {path.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => setPath([])}
                        className="inline-flex items-center gap-1 hover:underline"
                      >
                        <ChevronLeft className="h-3 w-3" />
                        {locationLabel}
                      </button>
                    ) : (
                      <span>{locationLabel}</span>
                    )}
                    {path.map((node, index) => (
                      <span key={node.id} className="inline-flex items-center gap-1">
                        <ChevronRight className="h-3 w-3" />
                        <button
                          type="button"
                          onClick={() => setPath(path.slice(0, index + 1))}
                          className="hover:underline"
                        >
                          {node.name}
                        </button>
                      </span>
                    ))}
                  </div>
                  <ul className="space-y-1">
                    {browseEntries.map((entry) => {
                      const isFile = entry.kind === 'file';
                      const checked = selected.has(entry.id);
                      return (
                        <li key={entry.id}>
                          <button
                            type="button"
                            className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm hover:bg-zinc-100 dark:hover:bg-zinc-900"
                            onClick={() => {
                              if (isFile) {
                                toggleSelection({ id: entry.id, name: entry.name });
                                return;
                              }
                              if (entry.children) setPath((prev) => [...prev, entry]);
                            }}
                          >
                            {isFile ? (
                              <input
                                type="checkbox"
                                checked={checked}
                                readOnly
                                className="pointer-events-none"
                              />
                            ) : (
                              <Folder className="h-4 w-4 text-zinc-400" />
                            )}
                            <span className="truncate">{entry.name}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>

        {selected.size > 0 ? (
          <div className="mt-3 rounded-lg border border-[var(--glass-border)] bg-[var(--glass-surface-soft)] p-2">
            <div className="mb-2 text-xs font-medium text-zinc-700 dark:text-zinc-200">
              Selected ({selected.size})
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Array.from(selected.values()).map((item) => (
                <span
                  key={item.id}
                  className="inline-flex max-w-full items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-xs dark:bg-zinc-800"
                >
                  <span className="truncate">{item.name}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${item.name}`}
                    onClick={() => toggleSelection(item)}
                    className="rounded-full p-0.5 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mt-3 flex items-center justify-between gap-2">
          <div className="text-xs text-muted">
            {importing && importProgress
              ? `Adding item ${importProgress.index} of ${importProgress.total}…`
              : null}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-lg px-3 py-2 text-sm"
              disabled={importing}
              onClick={() => {
                onOpenChange(false);
                reset();
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className={cn(BUTTON_PRIMARY, 'inline-flex items-center gap-2')}
              disabled={selected.size === 0 || importing}
              onClick={() => void runImport()}
            >
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Add{' '}
              {selected.size > 0
                ? `${selected.size} item${selected.size === 1 ? '' : 's'}`
                : 'items'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function LocationSection({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-1 border-b border-[var(--glass-border)] px-2 py-3', className)}>
      <p className={cn(MENU_SECTION_LABEL, 'px-2')}>{label}</p>
      {children}
    </div>
  );
}

function LocationRow({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm hover:bg-zinc-100 dark:hover:bg-zinc-900',
        active && SIDEBAR_ROW_ACTIVE,
      )}
    >
      <span className="shrink-0">{icon}</span>
      <span className="truncate">{label}</span>
    </button>
  );
}
